import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  loadPricesSnapshot,
  getUsdRateForDate,
  getUvaForDate,
  type PricesSnapshot,
} from '../libs/publicPricesSnapshot';
import { convertirMonto, formatMonedaSimbolo } from '../libs/moneda';

/**
 * Contexto de moneda para la sección de Administración.
 *
 * Tres modos:
 *  - 'ARS': tal cual.
 *  - 'USD': convierte por el dólar BLUE de la fecha del movimiento (estadisticasbcra).
 *  - 'constante': pesos constantes de HOY, ajustando por inflación con UVA:
 *      monto_constante = ars * (UVA_hoy / UVA_de_la_fecha).
 *
 * Toda la lógica de conversión vive acá (convertir/fmtMoneda/formatear); las páginas
 * solo consumen estos helpers. Persiste la elección en localStorage.
 */
export type Moneda = 'ARS' | 'USD' | 'constante';

interface MonedaContextValue {
  moneda: Moneda;
  setMoneda: (m: Moneda) => void;
  snapshot: PricesSnapshot | null;
  usdAvailable: boolean;
  uvaAvailable: boolean;
  usdRate: number | null; // blue más reciente
  uvaToday: number | null; // UVA más reciente
  rateForDate: (date: string | Date | null | undefined) => number | null; // blue de la fecha
  uvaForDate: (date: string | Date | null | undefined) => number | null;
  /** Convierte un monto ARS a la moneda actual (por la cotización/UVA de la fecha). */
  convertir: (ars: number, date?: string | Date | null) => number;
  /** Formatea un valor YA convertido, con el símbolo de la moneda actual. */
  fmtMoneda: (value: number) => string;
  /** Convierte + formatea un monto ARS con su fecha. Alias: formatMoneda. */
  formatear: (ars: number, date?: string | Date | null) => string;
  /** Alias de formatear (compat). */
  formatMoneda: (ars: number, date?: string | Date | null) => string;
  /** País del usuario (de localStorage, default 'Argentina'). */
  pais: string;
  setPais: (p: string) => void;
  /** Si es argentino → muestra el toggle USD/Const.; si no, solo ARS. */
  isArgentino: boolean;
  loading: boolean;
}

const MonedaContext = createContext<MonedaContextValue | null>(null);
const STORAGE_KEY = 'mcd:moneda';

export const MonedaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [moneda, setMonedaState] = useState<Moneda>(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v === 'USD' || v === 'constante' ? v : 'ARS';
    } catch {
      return 'ARS';
    }
  });
  const [snapshot, setSnapshot] = useState<PricesSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [pais, setPaisState] = useState<string>(() => {
    try {
      return localStorage.getItem('mcd:pais') || 'Argentina';
    } catch {
      return 'Argentina';
    }
  });
  const isArgentino = pais === 'Argentina';
  const setPais = useCallback((p: string) => {
    setPaisState(p);
    try {
      localStorage.setItem('mcd:pais', p);
    } catch {
      /* ignore */
    }
    // Si no es argentino, forzar ARS (sin toggle USD/Const.).
    if (p !== 'Argentina') {
      setMonedaState('ARS');
      try {
        localStorage.setItem(STORAGE_KEY, 'ARS');
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    loadPricesSnapshot()
      .then(setSnapshot)
      .finally(() => setLoading(false));
  }, []);

  const setMoneda = useCallback((m: Moneda) => {
    setMonedaState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      /* storage no disponible */
    }
  }, []);

  const usdAvailable = !!snapshot?.usd.available;
  const uvaAvailable = !!snapshot?.uva.available;
  const usdRate = snapshot?.usd.available ? snapshot.usd.rate : null;
  const uvaToday = snapshot?.uva.available ? snapshot.uva.today : null;

  const rateForDate = useCallback(
    (date: string | Date | null | undefined) => getUsdRateForDate(snapshot, date),
    [snapshot]
  );
  const uvaForDate = useCallback(
    (date: string | Date | null | undefined) => getUvaForDate(snapshot, date),
    [snapshot]
  );

  const convertir = useCallback(
    (ars: number, date?: string | Date | null) =>
      convertirMonto(
        ars,
        moneda,
        getUsdRateForDate(snapshot, date ?? null),
        getUvaForDate(snapshot, null),
        getUvaForDate(snapshot, date ?? null)
      ),
    [moneda, snapshot]
  );

  const fmtMoneda = useCallback(
    (value: number) => formatMonedaSimbolo(value, moneda),
    [moneda]
  );

  const formatear = useCallback(
    (ars: number, date?: string | Date | null) => fmtMoneda(convertir(ars, date)),
    [convertir, fmtMoneda]
  );

  return (
    <MonedaContext.Provider
      value={{
        moneda,
        setMoneda,
        snapshot,
        usdAvailable,
        uvaAvailable,
        usdRate,
        uvaToday,
        rateForDate,
        uvaForDate,
        convertir,
        fmtMoneda,
        formatear,
        formatMoneda: formatear,
        pais,
        setPais,
        isArgentino,
        loading,
      }}
    >
      {children}
    </MonedaContext.Provider>
  );
};

export function useMoneda(): MonedaContextValue {
  const ctx = useContext(MonedaContext);
  if (!ctx) throw new Error('useMoneda debe usarse dentro de <MonedaProvider>');
  return ctx;
}

/**
 * Variante segura: devuelve el contexto o null si no hay provider (ej. Layout
 * renderizado fuera de rutas admin). Permite mostrar el toggle solo donde
 * corresponde sin romper páginas no-admin.
 */
export function useMonedaSafe(): MonedaContextValue | null {
  return useContext(MonedaContext);
}
